import React, { useMemo } from 'react';
import { View, StyleSheet } from '@react-pdf/renderer';
import { Transaction, warranties } from 'src/types';
import { ViewWarrantyRow } from './ViewWarranty';
import { TextBoldWarranty, TextRegularWarranty } from './TextWarranty';
import { cloneDeep } from 'lodash';
import { ProductTypeEnum } from 'src/enums';

interface Props {
  items?: Transaction[];
  isBlankPage?: boolean;
  isLastPage?: boolean;
  kachiPoints?: number; // this is negative number
  voucherDiscount?: number;
  voucher_code?: string;
}

const styles = StyleSheet.create({
  main: {
    marginTop: 10,
    borderRightWidth: 1
  },
  header: {
    height: 20
  },
  headerColumn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderBottomWidth: 1
  },
  itemColumnContainer: {
    minHeight: 15
  },
  itemColumn: {
    paddingVertical: 2,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  descriptionOrSnText: {
    paddingHorizontal: 5
  }
});

const flexSizeOfColumns = {
  qty: 0.5,
  description: 3,
  unitPrice: 1,
  amount: 1
};

const headerColumns = [
  { flex: flexSizeOfColumns.qty, title: 'Qty' },
  {
    flex: flexSizeOfColumns.description,
    title: 'Product Name / Serial No.'
  }
];

const kachiPointsTitle = 'KACHI POINTS USED:';
const voucherDiscountTitle = 'VOUCHER CODE:';

type ItemPerLineData = {
  product_id?: number;
  quantity?: number;
  title?: string;
  retail_price?: number;
  amount?: number;
  hidden?: boolean;
  spend_kachi_points?: number;
};

export const RenderItemsTable = ({
  items,
  isBlankPage,
  kachiPoints,
  isLastPage,
  voucherDiscount,
  voucher_code
}: Props) => {
  const numberOfLinesOfItems = useMemo(() => {
    let actualRowsTaken = 0;
    const clonedTransactionItems = cloneDeep(items || []);

    clonedTransactionItems?.forEach((item) => {
      const itemName = item.product_name;
      if (itemName) {
        actualRowsTaken++;
      }
      if (
        item?.product_type !== ProductTypeEnum.Consumable &&
        item?.serial_nos &&
        item?.serial_nos?.length > 0
      ) {
        item?.serial_nos?.forEach((x) => {
          if (x) {
            actualRowsTaken++;
          }
        });
      }
      item.warranty_duration !== 0 && actualRowsTaken++;
      item.category_id === 39 &&
        item.category_name === 'Laptop' &&
        actualRowsTaken++;
      
        
      if (item.bundle_items != undefined) {
        item.bundle_items.forEach((bundleItem) => {
          bundleItem.product_name != null && actualRowsTaken++;
          bundleItem.serial_no != null && actualRowsTaken++;
          bundleItem.warranty_duration !== 0 && actualRowsTaken++
        })
      }
    });

    if (isLastPage && (kachiPoints || voucherDiscount)) {
      if (kachiPoints !== 0) actualRowsTaken++;
      if (voucherDiscount !== 0) actualRowsTaken++;
    }

    actualRowsTaken++;
    return actualRowsTaken;
  }, [isLastPage, items, kachiPoints, voucherDiscount]);

  const itemsWithNothingToFollow = useMemo(() => {
    const limitOfLinePerPage = 19;
    if (numberOfLinesOfItems < limitOfLinePerPage) {
      const numOfDummyLinesToAdd = limitOfLinePerPage - numberOfLinesOfItems;
      const clonedTransactionItems = cloneDeep(items);

      if (isLastPage) {
        const hasKachiPoints = kachiPoints !== undefined && kachiPoints !== 0;
        const hasVoucherDiscount =
          voucherDiscount !== undefined && voucherDiscount !== 0;

        if (hasKachiPoints) {
          clonedTransactionItems?.push({
            product_name: kachiPointsTitle,
            retail_price: undefined,
            amount: Math.abs(kachiPoints)
          });
        }

        if (hasVoucherDiscount) {
          clonedTransactionItems?.push({
            product_name: `${voucherDiscountTitle} ${voucher_code}`,
            retail_price: undefined,
            amount: Math.abs(voucherDiscount)
          });
        }
      }

      clonedTransactionItems?.push({
        product_name:
          isBlankPage || !isLastPage ? ' ' : '-------- Nothing Follows --------'
      });

      const dummyLines = Array.from({ length: numOfDummyLinesToAdd }, () => ({
        product_name: '   '
      }));
      clonedTransactionItems?.push(...dummyLines);

      return clonedTransactionItems;
    }

    return items;
  }, [
    numberOfLinesOfItems,
    items,
    kachiPoints,
    voucher_code,
    voucherDiscount,
    isBlankPage,
    isLastPage
  ]);

  const itemsArrayOnPerLine = useMemo(() => {
    let newItemsArr: ItemPerLineData[] = [];
    const clonedItemsWithNothingFollows = cloneDeep(itemsWithNothingToFollow);

    clonedItemsWithNothingFollows?.forEach((x) => {
      if (x?.product_name) {
        newItemsArr?.push({
          product_id: x?.product_id,
          quantity: x?.quantity,
          title: x?.product_name,
          retail_price: x?.retail_price,
          amount: x?.amount,
          spend_kachi_points: x?.spend_kachi_points
        });
      }
      if (x?.serial_nos && x?.serial_nos?.length > 0) {
        x?.serial_nos?.forEach((sn) => {
          newItemsArr?.push({ title: sn });
        });
      }
      if (x?.warranty_duration) {
        const warrantyDuration = warranties?.find((warranty) => {
          return warranty?.value === x?.warranty_duration;
        });
        newItemsArr?.push({
          title: `WARRANTY: ${warrantyDuration?.name}${
            x.category_id === 39 && x.category_name === 'Laptop'
              ? '\nBATTERY WARRANTY: 1 YEAR - (365 DAYS)'
              : ''
          }`
        });
      }

      if (x.bundle_items != undefined) {
        x.bundle_items.forEach((item) => {
          if (item.product_name) {
            newItemsArr?.push({
              title: item.product_name,
              quantity: 1
            })
          }
          if (item.serial_no && item.serial_no.length > 0) {
            newItemsArr?.push({ title: item.serial_no});
          }
          if (item.warranty_duration) {
            const warrantyDuration = warranties?.find((warranty) => {
              return warranty?.value === item?.warranty_duration;
            });

            newItemsArr?.push({
              title: `WARRANTY: ${warrantyDuration?.name}`
            })
          }
        })
      }
    });

    return newItemsArr;
  }, [itemsWithNothingToFollow]);

  const displayQuantity = (item: ItemPerLineData) => {
    if (item.product_id) {
      return item.quantity || '1';
    }

    return item.quantity || '';
  };

  const ItemRow = ({ item }: { item: ItemPerLineData }) => {
    return (
      <ViewWarrantyRow
        style={{
          height: item?.title && item?.title?.length >= 40 ? 'auto' : 17
        }}
      >
        <View style={[styles.itemColumn, { flex: flexSizeOfColumns.qty }]}>
          <TextRegularWarranty>{displayQuantity(item)}</TextRegularWarranty>
        </View>
        <View
          style={[
            styles.itemColumn,
            { flex: flexSizeOfColumns.description, alignItems: 'flex-start' }
          ]}
        >
          <TextRegularWarranty style={styles.descriptionOrSnText}>
            {item?.title?.toUpperCase()}
          </TextRegularWarranty>
        </View>
      </ViewWarrantyRow>
    );
  };

  return (
    <View style={styles.main}>
      <ViewWarrantyRow style={styles.header}>
        {headerColumns.map((x) => (
          <View key={x.title} style={[styles.headerColumn, { flex: x.flex }]}>
            <TextBoldWarranty>{x.title}</TextBoldWarranty>
          </View>
        ))}
      </ViewWarrantyRow>
      {itemsArrayOnPerLine?.map((item, i) => (
        <ItemRow key={i} item={item} />
      ))}
    </View>
  );
};
